import moment from "moment";

/** Late if arrival is at or after 09:41 and before 13:00 (09:40 is on time). */
export function isLateArrivalTime(punchTime) {
  const m = moment(punchTime);
  if (!m.isValid()) return false;
  const dayStart = m.clone().startOf("day");
  const lateFrom = dayStart.clone().hour(9).minute(41).second(0).millisecond(0);
  const arrivalClose = dayStart.clone().hour(13).minute(0).second(0).millisecond(0);
  return m.isSameOrAfter(lateFrom) && m.isBefore(arrivalClose);
}

/**
 * Classify a machine punch in the user's local timezone.
 * Morning "in": same calendar day from midnight until 10:00 (includes early arrivals before 9:00).
 * Late from 09:41 until 13:00 (09:40 is on time; arrivals after 10:00 are also late).
 * Out: from 16:00 onward. Otherwise "other".
 */
export function classifyPunchForDisplay(punchTime) {
  const m = moment(punchTime);
  if (!m.isValid()) {
    return { kind: "other", isLate: false, label: "Punch time", shortLabel: "Punch" };
  }

  const dayStart = m.clone().startOf("day");
  const morningInClose = dayStart.clone().hour(10).minute(0).second(0).millisecond(0);
  const lateFrom = dayStart.clone().hour(9).minute(41).second(0).millisecond(0);
  const outOpen = dayStart.clone().hour(16).minute(0).second(0).millisecond(0);

  if (m.isSameOrAfter(dayStart) && m.isBefore(morningInClose)) {
    const isLate = m.isSameOrAfter(lateFrom);
    return { kind: "in", isLate, label: "In time", shortLabel: "In" };
  }

  if (m.isSameOrAfter(morningInClose) && m.isBefore(outOpen)) {
    return {
      kind: "other",
      isLate: isLateArrivalTime(punchTime),
      label: "In time",
      shortLabel: "In",
    };
  }

  if (m.isSameOrAfter(outOpen)) {
    return { kind: "out", isLate: false, label: "Out time", shortLabel: "Out" };
  }

  return { kind: "other", isLate: false, label: "Punch time", shortLabel: "Punch" };
}

export function formatPunchDisplay(punchTime) {
  return moment(punchTime).format("DD MMM YYYY, hh:mm:ss A");
}

export function getTodayMorningInPunch(logs) {
  const day = moment().startOf("day");
  const arrivalClose = day.clone().hour(13).minute(0).second(0).millisecond(0);
  const candidates = (logs || [])
    .filter((row) => {
      const m = moment(row.punchTime);
      return m.isValid() && m.isSame(day, "day") && m.isBefore(arrivalClose);
    })
    .sort((a, b) => moment(a.punchTime).valueOf() - moment(b.punchTime).valueOf());
  if (!candidates.length) return null;
  const row = candidates[0];
  const c = classifyPunchForDisplay(row.punchTime);
  return { row, c: { ...c, isLate: isLateArrivalTime(row.punchTime) } };
}

const EXPECTED_WORK_MINUTES = 8 * 60;

function punchDirectionFromType(punchType) {
  const t = String(punchType || "").toUpperCase();
  if (/\b(IN|CHECKIN|CHECK-IN|CLOCKIN|CLOCK.?IN)\b/.test(t)) return "in";
  if (/\b(OUT|CHECKOUT|CHECK-OUT|CLOCKOUT|CLOCK.?OUT)\b/.test(t)) return "out";
  if (t === "0" || t === "I") return "in";
  if (t === "1" || t === "O") return "out";
  return null;
}

function getPunchDirection(row) {
  const fromType = punchDirectionFromType(row.punchType);
  if (fromType) return fromType;
  const c = classifyPunchForDisplay(row.punchTime);
  if (c.kind === "in" || c.kind === "out") return c.kind;
  return "other";
}

function getDayPunchRowsSorted(logs, dayMoment) {
  const dayStart = dayMoment.clone().startOf("day");
  return (logs || [])
    .filter((row) => {
      const m = moment(row.punchTime);
      return m.isValid() && m.isSame(dayStart, "day");
    })
    .sort((a, b) => moment(a.punchTime).valueOf() - moment(b.punchTime).valueOf());
}

/**
 * Minutes from earliest punch to latest punch that local calendar day (when ≥2 punches).
 * Used for dashboard “8 hours on site” rule. We use chronological first→last instead of
 * inferring in/out from {@link classifyPunchForDisplay} (narrow morning/evening windows
 * mis-label mid-day punches and hide real last “out”, falsely showing the shortfall tile).
 * Single punch: span cannot be inferred → hasSpan false.
 */
export function computeFirstInLastOutMinutes(logs, dayMoment) {
  const rows = getDayPunchRowsSorted(logs, dayMoment);
  if (!rows.length) {
    return {
      minutes: 0,
      hasPunches: false,
      hasSpan: false,
      expectedMinutes: EXPECTED_WORK_MINUTES,
    };
  }

  if (rows.length < 2) {
    return {
      minutes: 0,
      hasPunches: true,
      hasSpan: false,
      expectedMinutes: EXPECTED_WORK_MINUTES,
    };
  }

  const first = moment(rows[0].punchTime);
  const last = moment(rows[rows.length - 1].punchTime);
  const minutes = Math.max(0, Math.round(last.diff(first, "milliseconds") / 60000));
  return {
    minutes,
    hasPunches: true,
    hasSpan: true,
    expectedMinutes: EXPECTED_WORK_MINUTES,
  };
}

/**
 * Sum paired in→out durations for one calendar day (local), using device punchType when clear,
 * otherwise {@link classifyPunchForDisplay} (in / out windows).
 */
export function computeWorkedMinutesFromDayLogs(logs, dayMoment) {
  const rows = getDayPunchRowsSorted(logs, dayMoment);

  if (!rows.length) {
    return { minutes: 0, hasPunches: false, expectedMinutes: EXPECTED_WORK_MINUTES };
  }

  const stack = [];
  let totalMs = 0;
  for (const row of rows) {
    const d = getPunchDirection(row);
    const m = moment(row.punchTime);
    if (d === "in") {
      stack.push(m);
    } else if (d === "out" && stack.length) {
      totalMs += m.diff(stack.pop(), "milliseconds");
    }
  }

  return {
    minutes: Math.max(0, Math.round(totalMs / 60000)),
    hasPunches: true,
    expectedMinutes: EXPECTED_WORK_MINUTES,
  };
}

export function formatDurationHoursMinutes(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
